import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { FieldValues, Path, PathValue, UseFormReturn } from 'react-hook-form';

import type { AnyScreenAction } from '@/shared/screen/types';

import type { FillAction, FillSpec } from './agent-fill-action';

/** Um campo gravado pelo Luy: o valor de antes (para desfazer) e o de agora. `via`: gravado pelo passo de outro campo. */
export interface AgentFillEntry {
  readonly field: string;
  readonly label: string;
  readonly before: unknown;
  readonly after: unknown;
  readonly via?: string;
}

export type AgentFillSkipReason = 'edited' | 'disabled' | 'readOnly' | 'unchanged';

/** O que volta ao modelo (U56): o que entrou, o que foi pulado e por quê, e os erros de validação traduzidos. */
export interface AgentFillResult {
  readonly applied: readonly { readonly field: string; readonly label: string; readonly before: unknown; readonly after: unknown }[];
  readonly skipped: readonly { readonly field: string; readonly label: string; readonly reason: AgentFillSkipReason; readonly message: string }[];
  readonly errors: readonly { readonly field: string; readonly label: string; readonly message: string }[];
}

export interface AgentFillApi<V extends FieldValues> {
  readonly form: UseFormReturn<V>;
  /** Grava como a pessoa gravaria (suja o campo); o motor registra antes e depois dos campos de `also`. */
  readonly setValue: <N extends Path<V>>(name: N, value: PathValue<V, N>) => void;
}

export interface AgentFillFieldOptions<V extends FieldValues> {
  /** Passo próprio no lugar do `setValue` (ex.: `changeProduct`, que aplica os padrões do produto). */
  readonly apply?: (value: unknown, api: AgentFillApi<V>) => void | Promise<void>;
  /** Campos que o passo também muda (rótulo `*Display`, padrões): entram no registro e no desfazer. */
  readonly also?: readonly Path<V>[];
}

export interface UseAgentFillOptions<V extends FieldValues> {
  readonly fields?: { readonly [K in Path<V>]?: AgentFillFieldOptions<V> };
  /** Campo desligado agora (status, aba, regra da tela): o motivo, que vai ao modelo como `skipped`. */
  readonly disabled?: (field: Path<V>, values: V) => MessageDescriptor | null | undefined;
  /** Formulário somente leitura: nada entra. */
  readonly readOnly?: boolean;
  /** No fluxo (U53): reabre os passos dos campos gravados (`fillReopener(flow)` de `shared/flow`). */
  readonly reopen?: (fields: readonly string[]) => void;
  /** A pessoa editou o campo (o Luy pula). Padrão: sujo ou tocado; a tela que suja campos sozinha (padrões) refina. */
  readonly edited?: (field: Path<V>, state: { readonly isDirty: boolean; readonly isTouched: boolean }) => boolean;
}

export interface AgentFill {
  /** Os campos marcados agora, na ordem em que entraram. */
  readonly entries: readonly AgentFillEntry[];
  /** Quantos campos estão com o selo agora (também os que vieram junto, como os padrões do produto). */
  readonly count: number;
  readonly apply: (input: Readonly<Record<string, unknown>>) => Promise<AgentFillResult>;
  /** Restaura o valor de antes de um campo (e dos que vieram junto com ele) ou, sem campo, de todos. */
  readonly undo: (field?: string) => void;
  readonly isFilled: (field: string) => boolean;
  /** A ação de preencher com o `run` montado: a tela a põe no manifesto do `useScreenAgent` no lugar da de fachada. */
  readonly bind: (action: FillAction) => AnyScreenAction;
}

const REASONS: Readonly<Record<AgentFillSkipReason, MessageDescriptor>> = {
  edited: msg`A pessoa já editou este campo; o Luy não sobrescreve.`,
  disabled: msg`Este campo não pode ser preenchido agora.`,
  readOnly: msg`O formulário está somente leitura.`,
  unchanged: msg`O campo já tem este valor.`,
};

const same = (a: unknown, b: unknown) => a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error && error.message.trim() !== '' ? error.message : fallback;
}

function createFillStore() {
  let entries: readonly AgentFillEntry[] = [];
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((listener) => listener());
  return {
    get: () => entries,
    find: (field: string) => entries.find((entry) => entry.field === field),
    put(entry: AgentFillEntry) {
      const at = entries.findIndex((item) => item.field === entry.field);
      entries = at < 0 ? [...entries, entry] : entries.map((item, index) => (index === at ? entry : item));
      emit();
    },
    drop(fields: ReadonlySet<string>) {
      const next = entries.filter((entry) => !fields.has(entry.field));
      if (next.length === entries.length) return;
      entries = next;
      emit();
    },
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

/**
 * O motor do preenchimento pelo Luy (U56, L2): aplica na ordem do spec, não sobrescreve o que a pessoa editou, marca a
 * origem (selo "Luy"), desfaz e devolve `{ applied, skipped, errors }`. Quando a pessoa edita um campo marcado, a
 * marca sai.
 */
export function useAgentFill<V extends FieldValues>(form: UseFormReturn<V>, spec: FillSpec, options: UseAgentFillOptions<V> = {}): AgentFill {
  const { i18n } = useLingui();
  const [store] = useState(createFillStore);
  const entries = useSyncExternalStore(store.subscribe, store.get, store.get);
  const latest = useRef(options);
  useEffect(() => {
    latest.current = options;
  });

  // Só a digitação (`type: 'change'`) é da pessoa; o `setValue` do motor e da tela não tira a marca.
  useEffect(() => {
    const subscription = form.watch((_values, { name, type }) => {
      if (type !== 'change' || !name) return;
      const touched = store.get().filter((entry) => entry.field === name || entry.via === name).map((entry) => entry.field);
      if (touched.length > 0) store.drop(new Set(touched));
    });
    return () => subscription.unsubscribe();
  }, [form, store]);

  const undo = useCallback(
    (field?: string) => {
      const chosen = store.get().filter((entry) => field === undefined || entry.field === field || entry.via === field);
      if (chosen.length === 0) return;
      for (const entry of [...chosen].reverse()) {
        const name = entry.field as Path<V>;
        form.setValue(name, entry.before as PathValue<V, Path<V>>, { shouldDirty: true });
        form.clearErrors(name);
      }
      store.drop(new Set(chosen.map((entry) => entry.field)));
      latest.current.reopen?.(chosen.map((entry) => entry.field));
    },
    [form, store],
  );

  const apply = useCallback(
    async (input: Readonly<Record<string, unknown>>): Promise<AgentFillResult> => {
      const opts = latest.current;
      const applied: AgentFillResult['applied'][number][] = [];
      const skipped: AgentFillResult['skipped'][number][] = [];
      const errors: AgentFillResult['errors'][number][] = [];
      const changed: string[] = [];
      const skip = (field: string, label: string, reason: AgentFillSkipReason, message?: MessageDescriptor | null) =>
        skipped.push({ field, label, reason, message: i18n._(message ?? REASONS[reason]) });
      const requested = spec.fields.filter((field) => Object.hasOwn(input, field.name) && input[field.name] !== undefined);
      for (const { name: field, label: labelText } of requested) {
        const name = field as Path<V>;
        const label = i18n._(labelText);
        const value = input[field];
        if (opts.readOnly === true) {
          skip(field, label, 'readOnly');
          continue;
        }
        const disabled = opts.disabled?.(name, form.getValues());
        if (disabled) {
          skip(field, label, 'disabled', disabled);
          continue;
        }
        const mine = store.find(field) !== undefined;
        const state = form.getFieldState(name);
        const edited = opts.edited ? opts.edited(name, state) : state.isDirty || state.isTouched;
        if (!mine && edited) {
          skip(field, label, 'edited');
          continue;
        }
        if (same(form.getValues(name), value)) {
          skip(field, label, 'unchanged');
          continue;
        }
        const own = opts.fields?.[name];
        const watched: Path<V>[] = [name, ...(own?.also ?? [])];
        const before = new Map<string, unknown>(watched.map((item) => [item, store.find(item)?.before ?? form.getValues(item)]));
        const api: AgentFillApi<V> = { form, setValue: (target, next) => form.setValue(target, next, { shouldDirty: true }) };
        try {
          if (own?.apply) await own.apply(value, api);
          else form.setValue(name, value as PathValue<V, Path<V>>, { shouldDirty: true });
        } catch (error) {
          errors.push({ field, label, message: messageOf(error, i18n._(msg`Não foi possível preencher este campo.`)) });
          continue;
        }
        for (const item of watched) {
          const after = form.getValues(item);
          const was = before.get(item);
          if (item !== name && same(was, after) && !store.find(item)) continue;
          store.put({ field: item, label, before: was, after, ...(item === name ? {} : { via: field }) });
          changed.push(item);
        }
        applied.push({ field, label, before: before.get(name), after: form.getValues(name) });
      }
      if (applied.length > 0) {
        const names = applied.map((item) => item.field as Path<V>);
        await form.trigger(names);
        for (const item of applied) {
          const message = form.getFieldState(item.field as Path<V>).error?.message;
          if (message) errors.push({ field: item.field, label: item.label, message });
        }
        opts.reopen?.(changed);
      }
      return { applied, skipped, errors };
    },
    [form, spec, store, i18n],
  );

  const isFilled = useCallback((field: string) => entries.some((entry) => entry.field === field), [entries]);
  const bind = useCallback((action: FillAction): AnyScreenAction => ({ ...action, run: ({ input }: { input: Readonly<Record<string, unknown>> }) => apply(input) }), [apply]);

  return useMemo(
    () => ({ entries, count: entries.length, apply, undo, isFilled, bind }),
    [entries, apply, undo, isFilled, bind],
  );
}
